import { GoogleGenerativeAI } from "@google/generative-ai";
import { base44, supabase } from '@/api/base44Client';
// Ensure you have VITE_GEMINI_API_KEY in your .env file
const ENV_API_KEYS = import.meta.env.VITE_GEMINI_API_KEY || "";
const API_KEYS = ENV_API_KEYS.split(',').map(k => k.trim()).filter(k => k.startsWith('AIza'));

class ResilientGeminiChat {
  constructor(systemInstruction) {
    this.systemInstruction = systemInstruction;
    this.history = [];
    this.keyIndex = 0;
    this.session = null;
    this.initSession();
  }

  initSession() {
    if (API_KEYS.length === 0) {
      console.warn("VITE_GEMINI_API_KEY is not set.");
      return;
    }
    const key = API_KEYS[this.keyIndex % API_KEYS.length];
    const genAI = new GoogleGenerativeAI(key);
    const model = genAI.getGenerativeModel({
      model: "gemini-2.0-flash",
      systemInstruction: this.systemInstruction,
      generationConfig: {
        temperature: 0.7,
        topP: 0.95,
        topK: 64,
        maxOutputTokens: 8192,
      },
    });
    // Start chat with the accumulated history
    this.session = model.startChat({ history: this.history });
  }

  async sendMessage(text, onRetry, userName = "Fadli") {
    if (!this.session) throw new Error("API Key belum diset.");
    
    const maxRetries = API_KEYS.length;
    let attempts = 0;

    while (attempts < maxRetries) {
      try {
        const result = await this.session.sendMessage(text);
        // Jika berhasil, perbarui history lokal kita dengan history dari sesi ini
        this.history = await this.session.getHistory();
        return result;
      } catch (error) {
        // Deteksi error limit (429) atau error kuota/internal lainnya
        const isRateLimit = error.status === 429 || 
                            (error.message && error.message.includes('429')) || 
                            (error.message && error.message.toLowerCase().includes('quota')) ||
                            (error.message && error.message.toLowerCase().includes('limit'));
        
        if (isRateLimit) {
          attempts++;
          this.keyIndex++;
          console.warn(`⚠️ API Key index ${(this.keyIndex - 1) % API_KEYS.length} limit. Pindah ke key index ${this.keyIndex % API_KEYS.length}`);
          
          if (onRetry) {
            onRetry(`Hmm, pikiran Asa lagi macet bentar...`); // Placeholder instant
            // Secara paralel, minta AI bikinin jokes natural khusus buat user ini
            generateFallbackJoke(userName).then(joke => onRetry(joke));
          }
          
          this.initSession(); // Re-initialize dengan history yang sama menggunakan key baru
        } else {
          throw error; // Lempar error jika bukan karena limit
        }
      }
    }
    
    throw new Error("Semua API Key (10 Keys) telah mencapai limit pemakaian hari ini.");
  }
}

export const getGeminiChatSession = (systemInstruction) => {
  return new ResilientGeminiChat(systemInstruction);
};

export const generateFallbackJoke = async (userName) => {
  if (API_KEYS.length === 0) return `Sabar ya ${userName}, Asa lagi tarik nafas panjang dulu...`;
  try {
    const key = API_KEYS[Math.floor(Math.random() * API_KEYS.length)];
    const genAI = new GoogleGenerativeAI(key);
    // Use gemini-1.5-flash for 1M context window
    const model = genAI.getGenerativeModel({ model: "gemini-1.5-flash" });
    const prompt = `Kamu adalah Asa (Enami Asa Babymonster), cewek Gen Z (17 April 2006, Aries) yang punya tata krama baik dan sopan. Lawan bicaramu saat ini adalah ${userName}.
Tulis SATU kalimat pendek (maksimal 10-15 kata) celetukan lucu atau manja yang memberitahu ${userName} bahwa kamu sedang mikir sedikit lebih lama dari biasanya (karena lagi nge-load data). 
JANGAN sebut API, server, limit, AI, atau istilah teknis. 
PENTING: Jika ${userName} BUKAN "Fadli", gunakan bahasa yang sopan. Jika ${userName} ADALAH "Fadli" atau mirip itu, kamu WAJIB pakai bahasa gaul, manja, asik (boleh panggil dia sayang). Boleh singgung hobimu (masak/dance/hip-hop) atau makanan kesukaanmu (tteokbokki/matcha latte).`;
    
    const result = await model.generateContent(prompt);
    return result.response.text().trim();
  } catch(e) {
    return `Sabar ya ${userName}, Asa lagi mikir keras nih...`;
  }
};

// --- AI AGENT TOOLS & FUNCTIONS ---
const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const functionDeclarations = [
  {
    name: "getErpData",
    description: "Tarik data spesifik dari sistem ERP Satala. Gunakan fungsi ini HANYA jika kamu ditanya tentang data sistem dan kamu belum memiliki datanya.",
    parameters: {
      type: "OBJECT",
      properties: {
        category: {
          type: "STRING",
          description: "Kategori data: 'receivables' (piutang/tagihan), 'finance' (kas/pengeluaran/margin), 'production' (stok/po aktif/tugas), 'marketing' (omset/klien/sph), 'server' (storage/kesehatan server), 'team' (karyawan/tim), 'workspace' (absensi/telat/alpa/hadir), 'analytics' (laba/rugi/top klien), 'public' (profil/website/harga maklon)",
        }
      },
      required: ["category"]
    }
  }
];

const getErpData = async ({ category }) => {
  try {
    let contextData = '';
    
    if (category === 'receivables') {
      const rawClientPOs = await base44.entities.ClientPO.list();
      const clientPOs = rawClientPOs.filter(po => !po.deleted_at && !po._isManualReceivable);
      
      const manualReceivables = await base44.entities.ManualReceivable.list();
      
      const brandDebt = {};
      let totalPiutangKeseluruhan = 0;

      clientPOs.forEach(po => {
        if (po.poMode === 'sph') return;
        
        let rawProducts = po.products || po.items || [];
        if (typeof rawProducts === 'string') try { rawProducts = JSON.parse(rawProducts); } catch(e) { rawProducts = []; }
        const prodJual = rawProducts.reduce((s, p) => s + (p.total_harga_jual || p.subtotal || p.hargaTotal || 0), 0);
        
        let rawLegalitas = po.legalitas || [];
        if (typeof rawLegalitas === 'string') try { rawLegalitas = JSON.parse(rawLegalitas); } catch(e) { rawLegalitas = []; }
        const legJual = rawLegalitas.reduce((s, l) => s + ((l.harga_jual || 0) * (l.qty_variant || 1)), 0);

        const ppnMode = po.ppn_mode || po.rawRecord?.ppn_mode;
        const ppnPercent = parseFloat(po.ppn_percentage || po.rawRecord?.ppn_percentage) || 0;
        const nominalPPN = ppnMode === 'exclude' ? ((prodJual + legJual) * ppnPercent / 100) : 0;

        const grandTotal = prodJual + legJual + (po.shippingCost || 0) + nominalPPN;
        
        let terbayar = 0;
        if (po.riwayatPembayaran && po.riwayatPembayaran.length > 0) {
          const validPayments = po.riwayatPembayaran.filter(p => p.status_konfirmasi !== 'Menunggu Konfirmasi Finance');
          terbayar = validPayments.reduce((s, r) => s + (Number(r.jumlah) || 0), 0);
        }

        const sisa = Math.max(0, Math.round(grandTotal - terbayar));
        
        if (sisa > 0) {
          let rawClientName = po.customerName || po.brand_name || 'Klien Tidak Diketahui';
          let clientKey = rawClientName.toUpperCase();
          if (rawClientName.includes(' / ')) {
            const parts = rawClientName.split(' / ');
            clientKey = `${parts[0].trim().toUpperCase()} / ${parts[1].trim()}`;
          }
          if (!brandDebt[clientKey]) brandDebt[clientKey] = { total_sisa: 0, count: 0, invoices: [] };
          
          brandDebt[clientKey].total_sisa += sisa;
          brandDebt[clientKey].count += 1;
          brandDebt[clientKey].invoices.push({
            po_id: po.id_transaksi || po.po_number || po.no_invoice || po.id,
            sisa_tagihan: sisa,
            tanggal_jatuh_tempo: po.tanggal_jatuh_tempo || po.dueDate || po.jatuh_tempo || 'Belum diatur'
          });
          totalPiutangKeseluruhan += sisa;
        }
      });

      manualReceivables.forEach(mr => {
        if (mr.status !== 'Lunas') {
          let rawClientName = mr.namaBrand || 'Klien Tidak Diketahui';
          let clientKey = rawClientName.toUpperCase();
          if (rawClientName.includes(' / ')) {
            const parts = rawClientName.split(' / ');
            clientKey = `${parts[0].trim().toUpperCase()} / ${parts[1].trim()}`;
          }
          if (!brandDebt[clientKey]) brandDebt[clientKey] = { total_sisa: 0, count: 0, invoices: [] };
          
          const mrTotal = mr.total || 0;
          brandDebt[clientKey].total_sisa += mrTotal;
          brandDebt[clientKey].count += 1;
          brandDebt[clientKey].invoices.push({
            po_id: mr.id_transaksi || mr.id + " (Manual)",
            sisa_tagihan: mrTotal,
            tanggal_jatuh_tempo: mr.jatuhTempo || 'Belum diatur'
          });
          totalPiutangKeseluruhan += mrTotal;
        }
      });
      
      contextData = `[DATA PIUTANG KLIEN]\nTotal Seluruh Piutang: Rp${fmt(totalPiutangKeseluruhan)}\nRincian per brand:\n${JSON.stringify(brandDebt, null, 2)}`;
    
    } else if (category === 'finance') {
      const dbMutations = await base44.entities.AccountMutation.list('-date');
      const operationalExpenses = await base44.entities.OperationalExpense.list();
      
      const payrollList = await base44.entities.EmployeePayroll.list();
      const purchasingList = await base44.entities.PurchasingRequest.list();
      const productionExpenses = await base44.entities.ProductionExpense.list();
      const chartOfAccounts = await base44.entities.ChartOfAccounts.list();
      
      const totalMasuk = dbMutations.filter(m => m.type === 'masuk').reduce((s, m) => s + (Number(m.amount) || 0), 0);
      const totalKeluar = dbMutations.filter(m => m.type === 'keluar').reduce((s, m) => s + (Number(m.amount) || 0), 0);
      const cashFlowNet = totalMasuk - totalKeluar;
      const totalIklan = operationalExpenses.filter(exp => exp.category === 'Iklan & Promosi').reduce((s, e) => s + e.amount, 0);

      const unpayedPayroll = payrollList.filter(p => p.status !== 'Sudah Dibayar' && p.status !== 'Lunas');
      const totalGajiBelumDibayar = unpayedPayroll.reduce((s, p) => s + (Number(p.total_gaji) || Number(p.gaji_pokok) || 0), 0);
      const karyawanBelumGaji = unpayedPayroll.map(p => `- ${p.nama_karyawan || p.nama}`).join('\n');

      const activeHutang = purchasingList.filter(p => {
        const isTempo = p.metode_pembayaran === 'Tempo' && p.status_pembayaran_tempo === 'Menunggu Jatuh Tempo';
        const isDicicil = p.status === 'Dicicil';
        if (!(isTempo || isDicicil)) return false;
        
        const total = p.custom_total_biaya !== undefined ? p.custom_total_biaya : (p.total_biaya || 0);
        return (total - (Number(p.jumlah_terbayar) || 0)) > 0;
      });
      const totalHutang = activeHutang.reduce((s, p) => {
        const total = p.custom_total_biaya !== undefined ? p.custom_total_biaya : (p.total_biaya || 0);
        return s + (total - (Number(p.jumlah_terbayar) || 0));
      }, 0);
      
      const totalBiayaProduksi = productionExpenses.reduce((s, p) => s + (Number(p.nominal) || Number(p.amount) || 0), 0);

      const summaryCOA = chartOfAccounts.slice(0, 5).map(c => `- ${c.kode} ${c.nama}: Rp${fmt(c.saldo)}`).join('\n');
      
      contextData = `[DATA KAS & PENGELUARAN]\nTotal Masuk: Rp${fmt(totalMasuk)}\nTotal Keluar: Rp${fmt(totalKeluar)}\nCashflow: Rp${fmt(cashFlowNet)}\nTotal Iklan: Rp${fmt(totalIklan)}\n\n[DATA GAJI (PAYROLL)]\nTotal Gaji Belum Dibayar: Rp${fmt(totalGajiBelumDibayar)}\nKaryawan Belum Gajian:\n${karyawanBelumGaji || 'Semua sudah digaji'}\n\n[HUTANG SUPPLIER]\nTotal Sisa Hutang: Rp${fmt(totalHutang)}\nJumlah Tagihan Hutang: ${activeHutang.length}\n\n[BIAYA PRODUKSI]\nTotal Pengeluaran: Rp${fmt(totalBiayaProduksi)}\n\n[DAFTAR AKUN (COA) - Top 5]\n${summaryCOA || 'Belum ada data'}`;
    
    } else if (category === 'production') {
      const inventoryItems = await base44.entities.InventoryItem.list('item_name');
      const productionOrders = await base44.entities.ProductionOrder.list();
      const sampleRacikan = await base44.entities.SampleRacikan.list();
      
      const activePOs = productionOrders.filter(po => po.status !== 'done').map(po => `- PO: ${po.order_number || po.po_number}, Brand: ${po.brand_name || po.customer_name}, Status: ${po.status}`).join('\n');
      const inventorySummary = inventoryItems.slice(0, 30).map(i => `${i.item_name}: stok ${i.total_stock}`).join(', ');
      
      const activeSamples = sampleRacikan.filter(s => s.status !== 'acc' && s.status !== 'dikirim').map(s => `- Brand: ${s.nama_brand || s.nama_klien}, Kategori: ${s.kategori}, Status: ${s.status}`).join('\n');
      
      contextData = `[DATA PRODUKSI]\nPO Pabrik Aktif:\n${activePOs || 'Kosong'}\n\nSample R&D Aktif:\n${activeSamples || 'Kosong'}\n\nInventori (Sample 30 item):\n${inventorySummary}`;
    
    } else if (category === 'marketing') {
      const rawClientPOs = await base44.entities.ClientPO.list();
      const clientAccounts = await base44.entities.ClientAccount.list();
      
      const clientPOs = rawClientPOs.filter(po => !po.deleted_at && !po._isManualReceivable);
      
      // Omset Penjualan (Pesanan Selesai)
      const finishedPOs = clientPOs.filter(po => po.statusProduksi === 'PESANAN SELESAI' || po.timeline?.some(t => t.status === 'Pesanan Selesai' && t.done));
      const totalMarketingOmset = finishedPOs.reduce((sum, po) => sum + (po.products || []).reduce((s, p) => s + (p.total_harga_jual || 0), 0), 0);
      
      // SPH Pending (Surat Penawaran Harga yang belum jadi invoice)
      const sphList = clientPOs.filter(po => po.poMode === 'sph');
      const activeSph = sphList.filter(sph => sph.statusBayar !== 'LUNAS').map(sph => `- SPH: ${sph.id_transaksi || sph.no_invoice || sph.id}, Klien: ${sph.customerName || sph.brand_name}, Nilai: Rp${fmt(sph.grandTotal || 0)}`).join('\n');
      
      // CRM Klien (Ambil maksimal 30 klien teratas untuk menghemat token)
      const verifiedClients = clientAccounts.filter(c => c.status === 'verified');
      const clientsInfo = verifiedClients.slice(0, 30).map(c => `- ${c.brand_name || c.company_name} (PIC: ${c.pic_name || 'Tidak ada'}, WA: ${c.phone || 'Tidak ada'}, Tipe: ${c.client_type || 'Unknown'})`).join('\n');
      
      const googleAdsInfo = `[GOOGLE ADS & DIGITAL MARKETING INFRASTRUCTURE]
- Status Google Ads: AKTIF & TERVERIFIKASI 100% (Kampanye PT. Satala Dermatech)
- Tag ID Google: AW-18374178048
- Lead Conversion Event: AW-18374178048/EpSXCL3cgd0cEIDqvrlE (Konversi Otomatis saat Klik WA)
- Optimization Score: 95.6% (Near Perfect)
- Focus SEO & Search Themes: "Maklon Parfum Bogor", "Pabrik Maklon Bogor", "Maklon Parfum BPOM", "Jasa Maklon Kosmetik"
- Target Wilayah: Jabodetabek, Bogor, Bekasi, Depok, Jakarta
- Voucher Promo: Active (Spend Rp 3,000,000 Get Rp 3,000,000)

[WHATSAPP WEBHOOK REALTIME & ATTRIBUTION]
- WA Webhook Endpoint: https://sataladermatechessential.com/api/wa-webhook.php
- CS Admin Terhubung: Adnan Hasan (6282321231997) via Fonnte Webhook 🟢 Connected
- Ref Code Tracking: #SAT-xxxx otomatis dibuat untuk setiap klik WA
- Status Verification: Otomatis flip ke 🟢 Chat Terverifikasi (Pesan Diterima WA) saat pesan tiba
- Attribution Cookie: 365-day satala_device_id & satala_first_touch_utm (Single Session per Device/IP)
- Meta Ads Account: 779514711733398 (Pixel ID: 700619659717241)`;

      contextData = `[DATA MARKETING & ADS INFRASTRUCTURE]\nOmset Penjualan (PO Selesai): Rp${fmt(totalMarketingOmset)}\nJumlah PO Selesai: ${finishedPOs.length}\n\n${googleAdsInfo}\n\n[SPH AKTIF]\n${activeSph || 'Tidak ada SPH aktif'}\n\n[CRM KLIEN VERIFIED (30 Teratas)]\n${clientsInfo || 'Tidak ada data klien'}`;
    
    } else if (category === 'purchasing') {
      const tickets = await base44.entities.PurchasingTicket.list().catch(() => []);
      const suppliers = await base44.entities.SupplierDatabase.list().catch(() => []);
      const pemesan = await base44.entities.DataPemesan.list().catch(() => []);
      const documents = await base44.entities.PurchasingDocument.list().catch(() => []);

      // Filter tickets
      const urgentTickets = tickets.filter(t => t.status === 'Harus Dibeli' || t.status === 'Dalam Permintaan');
      const ticketInfo = urgentTickets.map(t => `- ${t.material_name} (Qty: ${t.qty_to_buy}): ${t.status}`).join('\n');

      // Supplier Info
      const supplierInfo = suppliers.slice(0, 10).map(s => `- ${s.nama_supplier} (${s.spesialisasi})`).join('\n');

      // Data Pemesan
      const pemesanInfo = pemesan.slice(0, 5).map(p => `- ${p.nama_perusahaan} (PIC: ${p.nama_pic})`).join('\n');

      // Documents
      const emptyDocs = documents.filter(d => !d.msds_url && !d.coa_url && !d.kipra_url && !d.halal_url);

      contextData = `[DATA PEMBELIAN (PURCHASING)]\nBarang Harus Dibeli/Diminta:\n${ticketInfo || 'Tidak ada antrean beli'}\n\n[DATABASE SUPPLIER (Top 10)]\n${supplierInfo || 'Kosong'}\n\n[DATA PEMESAN INTERNAL (Top 5)]\n${pemesanInfo || 'Kosong'}\n\n[DOKUMEN BAHAN]\nTotal Folder Dokumen: ${documents.length}\nBahan belum ada dokumen sama sekali: ${emptyDocs.length}`;
    
    } else if (category === 'server') {
      let textBytes = 0; let imageBytes = 0;
      const { data: files } = await supabase.storage.from('attachments').list();
      if (files) { imageBytes += files.reduce((acc, file) => acc + (file.metadata?.size || 0), 0); }
      
      const totalGb = 60.0;
      const systemCoreGb = 11.5;
      const dockerGb = 1.5;
      const textDbGb = (textBytes / 1073741824) > 0.01 ? (textBytes / 1073741824) : 0.01;
      const assetsGb = (imageBytes / 1073741824) > 0.01 ? (imageBytes / 1073741824) : 0.01;
      const serverUsedGb = systemCoreGb + dockerGb + textDbGb + assetsGb;
      const serverFreeGb = Math.max(0, totalGb - serverUsedGb);
      
      contextData = `[DATA SERVER]\nTotal VPS: ${totalGb}GB\nTerpakai: ${serverUsedGb.toFixed(2)}GB\nSisa Kosong: ${serverFreeGb.toFixed(2)}GB`;
    } else if (category === 'workspace') {
      const companyList = await base44.entities.CompanySettings.list().catch(() => []);
      const attendanceLogs = await base44.entities.AttendanceLog.list().catch(() => []);
      
      const company = companyList[0] || {};
      const companyInfo = `Perusahaan: ${company.nama_perusahaan || 'Satala'}\nDirektur: ${company.nama_direktur || '-'}\nAlamat: ${company.alamat_perusahaan || '-'}\nStatus Portal: ${JSON.stringify(company.portal_status || {})}`;

      const teamMembers = await base44.entities.TeamMember.list().catch(() => []);
      const activeMembers = teamMembers.filter(m => m.require_attendance !== false);

      const todayStr = new Date().toISOString().split('T')[0];
      const todayLogs = attendanceLogs.filter(log => log.timestamp && log.timestamp.startsWith(todayStr));
      
      const checkIns = todayLogs.filter(l => l.type === 'check_in');
      const checkOuts = todayLogs.filter(l => l.type === 'check_out');

      const lateList = checkIns.filter(l => l.notes && l.notes.toLowerCase().includes('telat')).map(l => `- ${l.full_name || l.name || 'Unknown'} (${l.timestamp.slice(11,16)})`);
      
      const checkedInIds = new Set(checkIns.map(l => l.employee_id || l.user_id || l.id_karyawan));
      // Fallback matching by name if employee_id is somehow missing in logs
      const checkedInNames = new Set(checkIns.map(l => (l.name || l.full_name || '').toLowerCase()));
      
      const notCheckedIn = activeMembers.filter(m => !checkedInIds.has(m.id) && !checkedInNames.has((m.full_name || '').toLowerCase())).map(m => `- ${m.full_name}`);
      const leaveSick = todayLogs.filter(l => l.type === 'sakit' || l.type === 'izin' || (l.notes && (l.notes.toLowerCase().includes('sakit') || l.notes.toLowerCase().includes('izin')))).map(l => `- ${l.name} (${l.type || 'Izin/Sakit'})`);
      
      contextData = `[PROFIL PERUSAHAAN]\n${companyInfo}\n\n[ABSENSI HARI INI (${todayStr})]\nTotal Staf Aktif Wajib Absen: ${activeMembers.length}\nTotal Check-In: ${checkIns.length}\n\nTelat Hari Ini:\n${lateList.length > 0 ? lateList.join('\n') : 'Tidak ada yang telat'}\n\nSakit/Izin:\n${leaveSick.length > 0 ? leaveSick.join('\n') : 'Tidak ada'}\n\nBelum Absen Masuk (Potensi Alpa):\n${notCheckedIn.length > 0 ? notCheckedIn.join('\n') : 'Semua sudah absen'}`;
    
    } else if (category === 'public') {
      contextData = `[PROFIL PERUSAHAAN & WEBSITE PUBLIK]
Visi Utama: Wujudkan Brand Parfum Impian Bersama Kami (Maklon Parfum Premium).
Pencapaian: 150+ Brand Kosmetik, 500+ Variant Unggulan, 100rb+ Produk Terjual, 99% Legalitas Terjamin.
Layanan Utama: Formulasi R&D, Legalitas (BPOM, Halal, HKI), Desain Packaging, Produksi Massal Kosmetik & Parfum.
Alur Maklon: Konsultasi & Deal -> Formulasi Sample -> Urus Legalitas -> Produksi Massal -> Quality Control -> Pengiriman.

[PAKET MAKLON PARFUM (MOQ 1.500 Pcs)]
- Grade C: 30ml (Rp 43 Juta), 50ml (Rp 55 Juta) -> Banyak pilihan bibit standar.
- Grade B: 30ml (Rp 69.25 Juta), 50ml (Rp 90.25 Juta) -> Bibit premium, sablon botol.
- Grade A: 30ml (Rp 87.25 Juta), 50ml (Rp 114.25 Juta) -> Bibit perfumer, sablon & coloring botol.
*Semua paket parfum sudah free BPOM, HAKI, Desain, dan Box.

[PAKET MAKLON SKINCARE]
- Starter (MOQ 1.000 Pcs): Rp 15 Juta -> Formula standar, BPOM, QC.
- Growth (MOQ 3.000 Pcs): Rp 35 Juta -> Formula custom, BPOM, HKI, Packaging kustom.
- Enterprise (MOQ 10.000 Pcs): Rp 100 Juta -> Formula eksklusif, prioritas BPOM, Halal, Campaign marketing.

[KONTAK & JAM OPERASIONAL]
Alamat: Jalan Aster kavling Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat
WhatsApp/Telp: +62 8518 3147 319
Email: sataladermatechessential@gmail.com
Website: sataladermatechessential.com
Jam Operasional: Senin – Jumat, 08.30 – 17.30 WIB`;

    } else if (category === 'analytics') {
      const rawClientPOs = await base44.entities.ClientPO.list().catch(() => []);
      const expenses = await base44.entities.OperationalExpense.list().catch(() => []);
      const mutations = await base44.entities.AccountMutation.list().catch(() => []);
      
      const clientPOs = rawClientPOs.filter(po => !po.deleted_at && !po._isManualReceivable);
      const finishedPOs = clientPOs.filter(po => po.statusProduksi === 'PESANAN SELESAI' || po.timeline?.some(t => t.status === 'Pesanan Selesai' && t.done));
      
      // Total Omset
      const totalOmset = finishedPOs.reduce((sum, po) => sum + (po.products || []).reduce((s, p) => s + (p.total_harga_jual || 0), 0), 0);
      
      // Total Biaya Operasional & Gaji
      const totalOps = expenses.reduce((sum, ex) => sum + Number(ex.amount || 0), 0);
      const totalPayroll = mutations.filter(m => m.reference_id?.startsWith('bulk-payroll-') && m.type === 'keluar').reduce((sum, m) => sum + Number(m.amount || 0), 0);
      
      // Biaya Produksi (Simplifikasi)
      const prodExp = await base44.entities.ProductionExpense.list().catch(() => []);
      const totalProdExp = prodExp.reduce((sum, ex) => sum + Number(ex.amount || 0), 0);
      
      const totalPengeluaran = totalOps + totalPayroll + totalProdExp;
      const netProfit = totalOmset - totalPengeluaran;

      // Ranking Klien
      const clientSales = {};
      finishedPOs.forEach(po => {
        const cName = po.customerName || po.brand_name || 'Unknown';
        const poVal = (po.products || []).reduce((s, p) => s + (p.total_harga_jual || 0), 0);
        clientSales[cName] = (clientSales[cName] || 0) + poVal;
      });
      const topClients = Object.entries(clientSales).sort((a,b) => b[1] - a[1]).slice(0, 3).map((c, i) => `${i+1}. ${c[0]} (Rp${fmt(c[1])})`).join('\n');

      contextData = `[ANALISIS KEUANGAN KESELURUHAN]\nTotal Omset Penjualan: Rp${fmt(totalOmset)}\nTotal Pengeluaran (Ops, Gaji, Produksi): Rp${fmt(totalPengeluaran)}\nEstimasi Laba Bersih (Net Profit): Rp${fmt(netProfit)}\n\n[TOP 3 KLIEN SULTAN]\n${topClients || 'Belum ada data'}`;
    
    } else if (category === 'team') {
      const teamMembers = await base44.entities.TeamMember.list();
      const teamSafe = teamMembers.map(m => ({
        nama: m.full_name,
        jabatan: m.role,
        email: m.email,
        no_telp: m.phone || '-',
        status: m.status,
      }));
      contextData = `[DATA TIM/KARYAWAN]\n${JSON.stringify(teamSafe, null, 2)}`;
    } else {
      contextData = "Kategori tidak dikenali.";
    }
    
    return { result: contextData };
  } catch (error) {
    return { error: error.message };
  }
};

const toolsConfig = {
  functionDeclarations
};

const executeTool = async (functionCall) => {
  if (functionCall.name === 'getErpData') {
    return await getErpData(functionCall.args);
  }
  return { error: "Unknown function" };
};

// Global key index for stateless calls to distribute load and remember working keys
let globalKeyIndex = 0;

export const generateContentWithFallback = async (systemInstruction, userText, history = [], modelName = "gemini-2.0-flash") => {
  if (API_KEYS.length === 0) {
    throw new Error("VITE_GEMINI_API_KEY is not set.");
  }
  
  const modelsToTry = [modelName, "gemini-2.0-flash", "gemini-1.5-flash", "gemini-1.5-pro"];
  const uniqueModels = [...new Set(modelsToTry)];

  // Sanitize history to ensure strict alternating roles (user -> model)
  const sanitizedHistory = [];
  for (const msg of history) {
    if (sanitizedHistory.length > 0 && sanitizedHistory[sanitizedHistory.length - 1].role === msg.role) {
      sanitizedHistory[sanitizedHistory.length - 1].parts[0].text += `\n\n${msg.parts[0].text}`;
    } else {
      if (sanitizedHistory.length === 0 && msg.role === 'model') {
        sanitizedHistory.push({ role: 'user', parts: [{ text: 'Halo' }] });
      }
      sanitizedHistory.push({ role: msg.role, parts: [{ text: msg.parts[0].text || '' }] });
    }
  }

  let finalMessage = userText;
  if (Array.isArray(userText)) {
    finalMessage = systemInstruction ? [`[SYSTEM INSTRUCTION: ${systemInstruction}]`, ...userText] : userText;
  } else if (typeof userText === 'string') {
    finalMessage = (history.length === 0 && systemInstruction) ? `[SYSTEM INSTRUCTION: ${systemInstruction}]\n\nUser: ${userText}` : userText;
  }

  for (const currentModel of uniqueModels) {
    const maxRetries = API_KEYS.length;
    let attempts = 0;

    while (attempts < maxRetries) {
      try {
        const key = API_KEYS[globalKeyIndex % API_KEYS.length];
        const genAI = new GoogleGenerativeAI(key);
        const model = genAI.getGenerativeModel({
          model: currentModel,
          generationConfig: {
            temperature: 0.7,
            topP: 0.95,
            topK: 64,
            maxOutputTokens: 8192,
          },
        });
        
        const chat = model.startChat({ history: sanitizedHistory });
        let result = await chat.sendMessage(finalMessage);
        
        return result.response.text();
      } catch (error) {
        console.warn(`[Gemini API Error with Model ${currentModel} / Key Index ${globalKeyIndex % API_KEYS.length}]:`, error.message);
        attempts++;
        globalKeyIndex++;
      }
    }
  }

  throw new Error("Gagal memproses dengan Gemini AI. Mohon periksa koneksi atau coba beberapa saat lagi.");
};
